/**
 * The last founder's choice for one company, inside Delete account (119-07).
 *
 * When you are the last active member of a company, deleting your account
 * CLOSES it — it is never deleted outright (owner decisions #2–#4). You pick
 * how it is recorded: closed (the default, pre-selected), acquired (naming the
 * acquiring company), bankrupt, or closed AND its data deleted. Each option
 * says what it keeps and deletes, from the server's own codes in the preview.
 *
 * Deleting the company's data cannot be undone, so choosing it asks again in
 * an AppModal before the choice takes effect; dismissing that keeps the
 * previous choice.
 */

import React, { useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { Check, Minus, Building2 } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  ACQUIRER_NAME_MAX,
  OUTCOME_LABEL,
  deleteText,
  keepText,
  type CompanyOutcome,
  type CompanyOutcomeSelection,
  type DeletionPreviewCompany,
} from '@/lib/gdpr/account-deletion';

export function CompanyOutcomePicker({
  company,
  value,
  onChange,
  problem,
}: {
  company: DeletionPreviewCompany;
  value: CompanyOutcomeSelection | undefined;
  onChange: (next: CompanyOutcomeSelection) => void;
  /** A validation message for this company (e.g. the acquirer name is missing). */
  problem?: string | null;
}) {
  const colors = useThemeColors();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const name = company.name ?? 'Your company';
  const selected: CompanyOutcome = value?.outcome ?? company.defaultOutcome ?? 'closed';
  const chosen = company.choices.find((c) => c.outcome === selected) ?? company.choices[0];

  const pick = (outcome: CompanyOutcome) => {
    if (outcome === selected) return;
    if (!isWeb) Haptics.selectionAsync?.();
    if (outcome === 'delete_data') {
      setConfirmDelete(true);
      return;
    }
    onChange(outcome === 'acquired' ? { outcome, acquirerName: value?.acquirerName ?? '' } : { outcome });
  };

  return (
    <View
      testID={`company-outcome-${company.startupId}`}
      style={{
        borderRadius: 20,
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: problem ? colors.error : colors.border.light,
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-start" style={{ gap: 12, padding: 16, paddingBottom: 12 }}>
        <View
          className="shrink-0 items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: colors.surface.secondary }}
        >
          <Building2 size={18} color={colors.text.secondary} />
        </View>
        <View className="flex-1">
          <Text numberOfLines={2} style={{ color: colors.text.primary, fontSize: 15, fontWeight: '700' }}>
            {name}: you are its last member
          </Text>
          <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 19, marginTop: 2 }}>
            The company is not deleted. Choose how it stays on record — Close is selected unless you choose otherwise.
          </Text>
        </View>
      </View>

      <View accessibilityRole="radiogroup" style={{ paddingHorizontal: 8, paddingBottom: 8, gap: 4 }}>
        {company.choices.map((c) => {
          const on = c.outcome === selected;
          const danger = c.outcome === 'delete_data';
          const accent = danger ? colors.error : colors.primary[600];
          return (
            <View
              key={`outcome-${c.outcome}`}
              style={{
                borderRadius: 14,
                backgroundColor: on ? (danger ? colors.red.light : colors.primary[50]) : 'transparent',
                borderWidth: 1,
                borderColor: on ? accent : 'transparent',
                overflow: 'hidden',
              }}
            >
              <Pressable
                testID={`outcome-${company.startupId}-${c.outcome}`}
                accessibilityRole="radio"
                accessibilityState={{ checked: on }}
                accessibilityLabel={OUTCOME_LABEL[c.outcome].title}
                onPress={() => pick(c.outcome)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 12, minHeight: 52, paddingVertical: 10 }}
              >
                <View
                  className="shrink-0 items-center justify-center"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    borderWidth: 2,
                    borderColor: on ? accent : colors.border.medium,
                    backgroundColor: on ? accent : 'transparent',
                  }}
                >
                  {on && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.surface.primary }} />}
                </View>
                <View className="flex-1">
                  <Text numberOfLines={2} style={{ color: danger && on ? colors.error : colors.text.primary, fontSize: 15, fontWeight: '600' }}>
                    {OUTCOME_LABEL[c.outcome].title}
                  </Text>
                  <Text numberOfLines={2} style={{ color: colors.text.secondary, fontSize: 13, marginTop: 1 }}>
                    {OUTCOME_LABEL[c.outcome].summary}
                  </Text>
                </View>
              </Pressable>
            </View>
          );
        })}
      </View>

      {selected === 'acquired' && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 12, gap: 6 }}>
          <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 13, fontWeight: '600' }}>
            Acquiring company
          </Text>
          <TextInput
            testID={`acquirer-${company.startupId}`}
            value={value?.acquirerName ?? ''}
            onChangeText={(t) => onChange({ outcome: 'acquired', acquirerName: t })}
            maxLength={ACQUIRER_NAME_MAX}
            autoCorrect={false}
            accessibilityLabel="Name of the acquiring company"
            placeholder="e.g. Helvetia Holdings AG"
            placeholderTextColor={colors.text.tertiary}
            style={{
              height: 48,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: problem ? colors.error : colors.border.medium,
              backgroundColor: colors.surface.secondary,
              color: colors.text.primary,
              paddingHorizontal: 14,
              fontSize: 15,
            }}
          />
          <Text numberOfLines={2} style={{ color: colors.text.tertiary, fontSize: 12 }}>
            The company that bought it — not a person. It is shown with the company&apos;s status.
          </Text>
        </View>
      )}

      {problem && (
        <Text testID={`outcome-problem-${company.startupId}`} numberOfLines={2} style={{ color: colors.error, fontSize: 13, paddingHorizontal: 16, paddingBottom: 12 }}>
          {problem}
        </Text>
      )}

      {chosen && (
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light, padding: 16, gap: 6 }}>
          {chosen.keeps.map((k) => (
            <View key={`keep-${k}`} className="flex-row items-start" style={{ gap: 8 }}>
              <View className="shrink-0" style={{ paddingTop: 2 }}>
                <Check size={14} color={colors.success} />
              </View>
              <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18 }}>
                Kept: {keepText(k)}
              </Text>
            </View>
          ))}
          {chosen.deletes.map((d) => (
            <View key={`del-${d}`} className="flex-row items-start" style={{ gap: 8 }}>
              <View className="shrink-0" style={{ paddingTop: 2 }}>
                <Minus size={14} color={colors.error} />
              </View>
              <Text numberOfLines={2} className="flex-1" style={{ color: colors.text.secondary, fontSize: 13, lineHeight: 18 }}>
                Deleted: {deleteText(d)}
              </Text>
            </View>
          ))}
        </View>
      )}

      <AppModal visible={confirmDelete} onClose={() => setConfirmDelete(false)} title={`Delete ${name}'s data?`}>
        <Text numberOfLines={6} style={{ color: colors.text.secondary, fontSize: 15, lineHeight: 22 }}>
          When your account deletion runs, {name} is closed and its data is permanently deleted:{' '}
          {(company.choices.find((c) => c.outcome === 'delete_data')?.deletes ?? []).map(deleteText).join(', ').toLowerCase()}.
          The company page and its score history stay. This cannot be undone.
        </Text>
        <AppModal.Actions>
          <ModalAction label="Keep it sealed" onPress={() => setConfirmDelete(false)} />
          <ModalAction
            label="Delete the data"
            variant="destructive"
            onPress={() => {
              setConfirmDelete(false);
              onChange({ outcome: 'delete_data' });
            }}
          />
        </AppModal.Actions>
      </AppModal>
    </View>
  );
}
